const PAGE_SIZE = 250;

function hashString(value) {
  let hash = 2166136261;
  const text = String(value);
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seed) {
  let a = seed | 0;
  return function rng() {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function dayKey() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function pickIndices(count, limit, rng) {
  const size = Math.min(limit, count);
  const indices = Array.from({ length: count }, (_, index) => index);
  for (let i = 0; i < size; i++) {
    const j = i + Math.floor(rng() * (count - i));
    const swap = indices[i];
    indices[i] = indices[j];
    indices[j] = swap;
  }
  return indices.slice(0, size).sort((a, b) => a - b);
}

function artistFromProduct(product) {
  const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
  for (const tag of tags) {
    const parts = String(tag).split(':');
    if (parts[0].trim() === 'Artist' && parts[1]) {
      return parts.slice(1).join(':').trim();
    }
  }
  return (product.vendor || '').trim();
}

function productImage(product) {
  return (product.images && product.images[0]) || product.variants?.[0]?.featured_image || null;
}

function imageUrl(src, width) {
  if (!src) return '';
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch (error) {
    return src;
  }
}

function formatPrice(price) {
  const amount = Number(price);
  if (!Number.isFinite(amount)) return '';
  return `$${amount.toFixed(2)}`;
}

function isProductSoldOut(product) {
  if (!product) return false;
  if (product.available === false || product.available === 0) return true;
  const variants = product.variants;
  if (Array.isArray(variants) && variants.length) {
    return variants.every((variant) => variant.available === false);
  }
  return false;
}

function soldOutLabel() {
  return window.variantStrings?.soldOut || 'Sold Out';
}

function revealGrid(grid) {
  grid.classList.remove('collection-listing__grid--pending');
}

function updateTile(item, product) {
  const tile = item.querySelector('.product-tile');
  const link = item.querySelector('.product-tile__link');
  if (!tile || !link || !product?.handle) return false;

  const artist = artistFromProduct(product);
  const title = product.title || '';
  const href = `/products/${product.handle}`;
  const image = productImage(product);
  const price = formatPrice(product.variants?.[0]?.price);

  const soldOut = isProductSoldOut(product);
  tile.classList.toggle('product-tile--sold-out', soldOut);
  link.href = href;
  link.setAttribute(
    'aria-label',
    `${artist ? `${title} by ${artist}` : title}${soldOut ? ` — ${soldOutLabel()}` : ''}`
  );

  const media = item.querySelector('.product-tile__media');
  let badge = item.querySelector('.product-tile__sold-out');
  if (soldOut) {
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'product-tile__sold-out';
      (media || tile).prepend(badge);
    }
    badge.textContent = soldOutLabel();
  } else if (badge) {
    badge.remove();
  }

  const img = item.querySelector('.product-tile__image');
  if (img && image?.src) {
    img.src = imageUrl(image.src, 533);
    img.srcset = [165, 330, 533, 720, 900]
      .map((width) => `${imageUrl(image.src, width)} ${width}w`)
      .join(', ');
    img.width = image.width || 533;
    img.height = image.height || 533;
    img.alt = '';
  }

  let artistEl = item.querySelector('.product-tile__artist');
  const titleEl = item.querySelector('.product-tile__title');
  if (artist) {
    if (!artistEl && titleEl) {
      artistEl = document.createElement('p');
      artistEl.className = 'product-tile__artist';
      titleEl.before(artistEl);
    }
    if (artistEl) artistEl.textContent = artist;
  } else if (artistEl) {
    artistEl.remove();
  }

  if (titleEl) titleEl.textContent = title;

  const priceEl = item.querySelector('.product-tile__price');
  if (priceEl && price) {
    const currency = document.createElement('span');
    currency.className = 'product-tile__currency';
    currency.textContent = 'CAD';
    priceEl.replaceChildren(document.createTextNode(`${price} `), currency);
  }

  return true;
}

async function loadRandomProducts(grid) {
  const handle = grid.dataset.collectionHandle;
  const count = Number(grid.dataset.productsCount) || 0;
  const limit = Number(grid.dataset.limit) || 5;
  if (!handle || count < 1) {
    revealGrid(grid);
    return;
  }

  const rng = mulberry32(hashString(`${handle}:${dayKey()}`));
  const indices = pickIndices(count, limit, rng);
  const pages = [...new Set(indices.map((index) => Math.floor(index / PAGE_SIZE) + 1))];

  const fetched = await Promise.all(
    pages.map(async (page) => {
      const response = await fetch(
        `/collections/${encodeURIComponent(handle)}/products.json?limit=${PAGE_SIZE}&page=${page}`
      );
      if (!response.ok) throw new Error(`Failed to load ${handle}`);
      const data = await response.json();
      return { page, products: data.products || [] };
    })
  );

  const byPage = Object.fromEntries(fetched.map((entry) => [entry.page, entry.products]));
  const products = indices
    .map((index) => {
      const page = Math.floor(index / PAGE_SIZE) + 1;
      return byPage[page]?.[index % PAGE_SIZE];
    })
    .filter(Boolean);

  const items = [...grid.querySelectorAll('.collection-listing__item')];
  products.forEach((product, index) => {
    if (items[index]) updateTile(items[index], product);
  });

  revealGrid(grid);
}

function initRandomCollectionGrid(grid) {
  if (!grid || grid.dataset.randomReady === 'true') return;
  grid.dataset.randomReady = 'true';
  loadRandomProducts(grid).catch(() => revealGrid(grid));
}

function initRandomCollectionGrids(root) {
  (root || document).querySelectorAll('[data-random-collection]').forEach(initRandomCollectionGrid);
}

initRandomCollectionGrids(document);
document.addEventListener('shopify:section:load', (event) => {
  initRandomCollectionGrids(event.target);
});
